/* Collection hero "souffle" wave: raw WebGL, no library. Gold contour lines on
   a transparent canvas, from a domain-warped noise field tuned by data-*
   settings. Drawn inside a worker through OffscreenCanvas so a busy main thread
   cannot stutter it, at half resolution on low-end devices rather than frozen,
   static under reduced motion, self-disposing when the hero re-renders. */
(function () {
  'use strict';

  var bq = window.bq;
  if (!bq) return;

  // Renderer, owner of the loop and of the clock. Stringified into the worker
  // source below, so it has to stay self-contained: no outer reference, `self`
  // for the global. The CDN strips comments and whitespace but keeps
  // identifiers, which is what toString() relies on.
  function wave(canvas, cfg) {
    var VERT = 'attribute vec2 p; void main(){ gl_Position = vec4(p, 0.0, 1.0); }',
      FRAG = [
        'precision highp float;',
        'uniform vec2 u_res; uniform float u_time; uniform float u_gold; uniform float u_inten;',
        'float hash(vec2 p){ p = fract(p*vec2(123.34,345.45)); p += dot(p,p+34.345); return fract(p.x*p.y); }',
        'float noise(vec2 p){ vec2 i=floor(p),f=fract(p); vec2 u=f*f*(3.0-2.0*f);',
        '  float a=hash(i),b=hash(i+vec2(1,0)),c=hash(i+vec2(0,1)),d=hash(i+vec2(1,1));',
        '  return mix(mix(a,b,u.x),mix(c,d,u.x),u.y); }',
        'float fbm(vec2 p){ float v=0.0,a=0.5; for(int i=0;i<3;i++){ v+=a*noise(p); p*=2.0; a*=0.5; } return v; }',
        'void main(){',
        '  vec2 uv = gl_FragCoord.xy / u_res.xy;',
        '  float aspect = u_res.x / max(u_res.y,1.0);',
        '  vec2 p = vec2(uv.x*aspect, uv.y) * 2.4;',
        '  float t = u_time;',
        '  vec2 q = vec2(fbm(p + vec2(0.0, t*0.05)), fbm(p + vec2(5.2,1.3) - t*0.04));',
        '  float w = fbm(p*1.15 + q*0.9 + vec2(t*0.05, t*0.015));',
        '  float lf = w*2.0 - t*0.22 + uv.y*0.3;',       // x2 spacing (fewer cycles)
        '  float lg = fract(lf); float dl = min(lg, 1.0-lg);',
        '  float lw = 0.03 + 0.055*u_inten;',            // line width (intensity)
        '  float line = smoothstep(lw, 0.0, dl);',        // crisp band
        '  vec3 GOLD = vec3(0.85, 0.70, 0.35);',
        '  float a = line * u_gold;',                     // line opacity (gold % from the setting)
        '  gl_FragColor = vec4(GOLD * a, a);',           // premultiplied: floats over the paper
        '}'
      ].join('\n');

    var gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) return null; // no WebGL: the section's paper background shows through

    function sh(type, src) {
      var s = gl.createShader(type);
      gl.shaderSource(s, src); gl.compileShader(s);
      return gl.getShaderParameter(s, gl.COMPILE_STATUS) ? s : null;
    }
    var vs = sh(gl.VERTEX_SHADER, VERT), fs = sh(gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) return null;
    var prog = gl.createProgram();
    gl.attachShader(prog, vs); gl.attachShader(prog, fs); gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return null;
    gl.useProgram(prog);

    var buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    var loc = gl.getAttribLocation(prog, 'p');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    var uRes = gl.getUniformLocation(prog, 'u_res'),
      uTime = gl.getUniformLocation(prog, 'u_time'),
      uGold = gl.getUniformLocation(prog, 'u_gold'),
      uInten = gl.getUniformLocation(prog, 'u_inten');

    // Worker animation frames come with OffscreenCanvas, but not in every
    // engine that ships the canvas: a timer keeps the same shape where absent.
    var rq = self.requestAnimationFrame
      ? self.requestAnimationFrame.bind(self)
      : function (cb) { return setTimeout(function () { cb(Date.now()); }, 16); },
      cn = self.cancelAnimationFrame
        ? self.cancelAnimationFrame.bind(self)
        : function (id) { clearTimeout(id); };

    var raf = null, on = false, last = 0, tAcc = 0, first = true;

    function draw() {
      gl.uniform2f(uRes, canvas.width, canvas.height);
      gl.uniform1f(uTime, tAcc);
      gl.uniform1f(uGold, cfg.gold);
      gl.uniform1f(uInten, cfg.inten);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      if (first) { first = false; cfg.onFirst(); }
    }
    // Every frame, no skipping: an uneven cadence reads as a stutter, and the
    // fragment cost is what dpr is for.
    function loop(now) {
      if (!on) { raf = null; return; }
      if (last === 0) last = now;                        // resume: no time leap
      else {
        tAcc += Math.min(now - last, 100) * 0.001 * (0.3 + cfg.speed * 1.4);
        last = now;
      }
      draw();
      raf = rq(loop);
    }
    // CSS pixels in, device pixels out. dpr 1, or 0.5 on low-end devices where
    // CSS stretches the smaller buffer back over the hero.
    function size(w, h) {
      w = Math.max(1, Math.round(w * cfg.dpr));
      h = Math.max(1, Math.round(h * cfg.dpr));
      if (w !== canvas.width || h !== canvas.height) { canvas.width = w; canvas.height = h; }
      gl.viewport(0, 0, w, h);
      draw();
    }
    function run(v) {
      on = !!v && cfg.animate;
      if (on) { if (raf === null) { last = 0; raf = rq(loop); } }
      else if (raf !== null) { cn(raf); raf = null; }
    }
    function stop() {
      run(false);
      var ext = gl.getExtension('WEBGL_lose_context');
      if (ext) ext.loseContext();
    }
    return { size: size, run: run, stop: stop };
  }

  // Worker side: message plumbing only. Stringified into the worker source,
  // never called on this thread.
  function host() {
    var w = null;
    self.onmessage = function (e) {
      var m = e.data;
      if (m.t === 'stop') { if (w) w.stop(); self.close(); return; }
      if (m.t === 'init') {
        w = wave(m.canvas, {
          gold: m.gold, inten: m.inten, speed: m.speed, dpr: m.dpr, animate: m.animate,
          onFirst: function () { self.postMessage({ t: 'first' }); }
        });
        if (!w) { self.close(); return; }
        w.size(m.w, m.h);
        w.run(m.run);
        return;
      }
      if (!w) return;
      if (m.t === 'size') w.size(m.w, m.h);
      else if (m.t === 'run') w.run(m.on);
    };
  }

  // Assets are served from the Shopify CDN, so a worker cannot be loaded by
  // URL (different origin): it is built from this source instead.
  var SRC = '"use strict";\n' + wave.toString() + '\n' + host.toString() + '\nhost();';

  function num(v, d) { v = parseFloat(v); return isNaN(v) ? d : v; }

  var idle = window.requestIdleCallback || function (cb) { return setTimeout(cb, 80); },
    OFFSCREEN = typeof Worker !== 'undefined' && typeof OffscreenCanvas !== 'undefined',
    live = [];

  // A filter re-render swaps the canvas out of the DOM with no event to hear:
  // the orphans, and their workers, are collected on the next init.
  function reap() {
    live = live.filter(function (w) {
      if (w.el.isConnected) return true;
      w.off();
      return false;
    });
  }

  function setup(canvas) {
    var d = canvas.dataset,
      cfg = {
        gold: num(d.gold, 100) / 100,
        inten: num(d.inten, 50) / 100,
        speed: num(d.speed, 20) / 100,
        // Low-end devices get a quarter of the pixels rather than a still
        // image: cheaper than dropping frames, and the wave keeps moving.
        dpr: bq.liteDevice() ? 0.5 : 1,
        animate: !bq.reducedMotion()
      };

    var worker = null, local = null, ro = null, io = null;
    var visible = true, shown = false, stopped = false;

    // Fade in from JS, robust to any prior opacity/transition state (rapid
    // navigation can leave one mid-flight): kill the transition, commit 0, then
    // restore it and go to 1 so the fade always starts clean. CSS holds the
    // duration and the reduced-motion opt-out.
    function reveal() {
      if (shown) return;
      shown = true;
      var s = canvas.style;
      s.transition = 'none';
      s.opacity = '0';
      void canvas.offsetWidth;
      s.transition = '';
      s.opacity = '1';
    }
    function cw() { return Math.max(1, Math.round(canvas.clientWidth)); }
    function ch() { return Math.max(1, Math.round(canvas.clientHeight)); }
    function size() {
      if (worker) worker.postMessage({ t: 'size', w: cw(), h: ch() });
      else if (local) local.size(cw(), ch());
    }
    function run() {
      var on = visible && !document.hidden;
      if (worker) worker.postMessage({ t: 'run', on: on });
      else if (local) local.run(on);
    }
    // Nothing to do on navigation: a cross-document View Transition unloads the
    // document, and the worker goes with it.
    function stop() {
      if (stopped) return;
      stopped = true;
      if (ro) ro.disconnect();
      else window.removeEventListener('resize', size);
      if (io) io.disconnect();
      document.removeEventListener('visibilitychange', run);
      // The worker releases the GL context and closes itself on this message.
      if (worker) { worker.postMessage({ t: 'stop' }); worker = null; }
      if (local) { local.stop(); local = null; }
    }

    if (OFFSCREEN && 'transferControlToOffscreen' in canvas) {
      var url = URL.createObjectURL(new Blob([SRC], { type: 'text/javascript' }));
      try { worker = new Worker(url); } catch { worker = null; } // a CSP may refuse a blob worker
      URL.revokeObjectURL(url);
    }
    if (worker) {
      worker.onmessage = function (e) { if (e.data && e.data.t === 'first') reveal(); };
      var off = canvas.transferControlToOffscreen();
      worker.postMessage({
        t: 'init', canvas: off, w: cw(), h: ch(),
        dpr: cfg.dpr, gold: cfg.gold, inten: cfg.inten, speed: cfg.speed,
        animate: cfg.animate, run: !document.hidden
      }, [off]);
    } else {
      // Same renderer on the main thread, minus the isolation.
      local = wave(canvas, {
        gold: cfg.gold, inten: cfg.inten, speed: cfg.speed, dpr: cfg.dpr,
        animate: cfg.animate, onFirst: reveal
      });
      if (!local) return;
      size();
      run();
    }

    if (window.ResizeObserver) { ro = new ResizeObserver(size); ro.observe(canvas); }
    else window.addEventListener('resize', size);
    document.addEventListener('visibilitychange', run);
    if (window.IntersectionObserver) {
      io = new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
        run();
      });
      io.observe(canvas);
    }

    canvas.bqWaveOff = stop;
    live = live.concat([{ el: canvas, off: stop }]);

    // The first rendered frame opens the fade; the timer is only its floor.
    setTimeout(reveal, 300);
  }

  // Defer setup to idle so the ~30ms context creation stays off the navigation
  // path; the fade-in then hides the warm-up.
  function initWave(canvas) {
    reap();
    if (!canvas || canvas.dataset.bqWaveOn) return;
    canvas.dataset.bqWaveOn = '1';
    idle(function () { if (canvas.isConnected) setup(canvas); }, { timeout: 600 });
  }

  window.bqCollectionWave = initWave;

  function scan(scope) {
    scope.querySelectorAll('.bq-col-hero__wave').forEach(initWave);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(document); });
  } else {
    scan(document);
  }

  document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    e.target.querySelectorAll('.bq-col-hero__wave').forEach(function (c) {
      if (c.bqWaveOff) c.bqWaveOff();
    });
  });
})();
